import { MCPServerIdMembers } from '../mcp';
import type { MCPServerId, McpServerIdCamelCase } from '../mcp';
import { camelCase } from '../strings';

/**
 * Disable MCP servers here by including the Server ID in this set.
 */
const disabledByDefault = new Set<MCPServerId>([
  'APIIRO',
  'ASHBY_NATOMA',
  'TORII',
  'AVALARA_DEV_DOCUMENTATION',
  'AVALARA_TAX_CONTENT',
  'AVATAX',
  'AZURE_DEVOPS',
  'BIGQUERY_REMOTE',
  'BRAZE',
  'BULLHORN_CRM',
  'CHECKMARX',
  'CLOUDZERO',
  'CNA_GWRE_DEVELOPER_ASSISTANT',
  'CNA_JUTRO_DEVELOPER_ASSISTANT',
  'CYERA_DATAPORT',
  'DATAHUB',
  'FULLSTORY_LEXICON',
  'GENAI_TOOLBOX_BIGQUERY',
  'GENAI_TOOLBOX_LOOKER',
  'GENAI_TOOLBOX_ORACLEDB',
  'GENAI_TOOLBOX_POSTGRES',
  'GITHUB_OAUTH',
  'GITHUB_STDIO',
  'GITLAB_REMOTE',
  'GLEAN',
  'GONG',
  'GONG_COMMUNITY',
  'GOOGLE_CALENDAR',
  'GOOGLE_CALENDAR_OFFICIAL',
  'GOOGLE_CHAT',
  'GOOGLE_DOCS',
  'GOOGLE_DRIVE',
  'GOOGLE_DRIVE_OFFICIAL',
  'GOOGLE_FORMS',
  'GOOGLE_GMAIL',
  'GOOGLE_GMAIL_OFFICIAL',
  'GOOGLE_PEOPLE',
  'GOOGLE_SHEETS',
  'GOOGLE_SLIDES',
  'GREENHOUSE',
  'ITERABLE',
  'LAUNCHDARKLY_OFFICIAL',
  'LUCID',
  'MICROSOFT_GRAPH_AND_ARM',
  'MIRO_OFFICIAL',
  'NETSUITE',
  'NEW_RELIC',
  'NOTEBOOKLM',
  'OKTA_NATOMA',
  'SALESFORCE_REMOTE',
  'SENTINELONE',
  'SERVICENOW_REMOTE',
  'SLACK_BETA',
  'SOURCEBOT',
  'SPLUNK',
  'SUPERNOVA',
  'SNOWFLAKE',
  'TERADATA',
  'UPWIND',
  'VEGA',
  'POWER_BI',
  'MOCK_SERVER'
]);

const serverIdFeatureSwitchObjects: {
  [K in McpServerIdCamelCase]: {
    doc: string;
    format: BooleanConstructor;
    default: boolean;
    env: string;
  };
} = MCPServerIdMembers.reduce(
  (acc, screamingCaseServerKey) => {
    const camelCaseServerKey = camelCase(screamingCaseServerKey);
    acc[camelCaseServerKey as McpServerIdCamelCase] = {
      doc: `Enables the "${camelCaseServerKey}" MCP server`,
      format: Boolean,
      default: !disabledByDefault.has(screamingCaseServerKey as MCPServerId),
      env: `FEATURE_SWITCH_MCP_SERVER_${screamingCaseServerKey}`
    };
    return acc;
  },
  {} as {
    [K in McpServerIdCamelCase]: {
      doc: string;
      format: BooleanConstructor;
      default: boolean;
      env: string;
    };
  }
);

export const FeatureSwitches = {
  testFeatureSwitch: {
    doc: 'Not to be used for anything - a useful value for unit testing feature switch-checking behavior',
    format: Boolean,
    default: false,
    env: 'DO_NOT_USE'
  },
  integrationAzureKV: {
    doc: 'Switch to enable or disable the Azure Key Vault integration',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_INTEGRATION_AZURE_KEYVAULT'
  },
  integrationEntra: {
    doc: 'Switch to enable or disable the Entra integration',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_INTEGRATION_ENTRA'
  },
  integrationServiceNow: {
    doc: 'Switch to enable or disable the Service Now integration',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_INTEGRATION_SERVICENOW'
  },
  integrationSnowflake: {
    doc: 'Switch to enable or disable the Snowflake integration',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_INTEGRATION_SNOWFLAKE'
  },
  integrationSlack: {
    doc: 'Switch to enable or disable the Slack integration',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_INTEGRATION_SLACK'
  },
  integrationSplunk: {
    doc: 'Switch to enable or disable the Splunk integration',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_INTEGRATION_SPLUNK'
  },
  integrationSplunkHec: {
    doc: 'Switch to enable or disable the Splunk HEC (SIEM export) integration',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_INTEGRATION_SPLUNK_HEC'
  },
  integrationObserveOtlp: {
    doc: 'Switch to enable or disable the Observe OTLP (SIEM export) integration',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_INTEGRATION_OBSERVE_OTLP'
  },
  processActivities: {
    doc: 'Enables updating related entities (service identities and resources) by processing saved activities',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_PROCESS_ACTIVITIES'
  },
  processAWSRoleActivity: {
    doc: 'Enables updating usage data of roles by processing AWS activities',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_PROCESS_AWS_ROLE_ACTIVITY'
  },
  processAWSTransformToAuthResources: {
    doc: 'Enables creating authresource transform in AWSActivityProcessor',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_PROCESS_AWS_TRANSFORM_TO_AUTHRESOURCES'
  },
  skipLatestActivityCheck: {
    doc: '[TESTING] Causes the activity ingest to skip a lookup for a latest activity timestamp and pull the default initial ingest window',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_SKIP_LATEST_ACTIVITY_CHECK'
  },
  ingestSalesforceMetadata: {
    doc: 'Enables ingestion of Salesforce Metadata augmentation',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_INGEST_SALESFORCE_METADATA'
  },
  ingestGoogleWorkspaceService: {
    doc: 'Enables ingestion of Google Workspace 3P services',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_INGEST_GOOGLE_WORKSPACE_SERVICE'
  },
  ingestOktaOIN: {
    doc: 'Enables ingestion of Okta API Service Integrations from OIN',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_INGEST_OKTA_OIN'
  },
  displayInferredServiceIdentities: {
    doc: 'Display inferred service identities in the UI',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_DISPLAY_INFERRED_SERVICE_IDENTITIES'
  },
  processSalesforceActivities: {
    doc: 'Enable Salesforce Activity Processor',
    format: Boolean,
    default: false,
    env: 'PROCESS_SALESFORCE_ACTIVITIES'
  },
  processSnowflakeActivities: {
    doc: 'Enable Snowflake Activity Processor',
    format: Boolean,
    default: false,
    env: 'PROCESS_SNOWFLAKE_ACTIVITIES'
  },
  processDatadogActivities: {
    doc: 'Enable Datadog Activity Processor',
    format: Boolean,
    default: false,
    env: 'PROCESS_DATADOG_ACTIVITIES'
  },
  processSlackActivities: {
    doc: 'Enable Slack Activity Processor',
    format: Boolean,
    default: false,
    env: 'PROCESS_SLACK_ACTIVITIES'
  },
  disableDatadogActivityIngest: {
    doc: 'Stop saving Datadog Activities',
    format: Boolean,
    default: false,
    env: 'DISABLE_DATADOG_ACTIVITY_INGEST'
  },
  disableSalesforceActivityIngest: {
    doc: 'Stop saving Salesforce Activities',
    format: Boolean,
    default: false,
    env: 'DISABLE_SALESFORCE_ACTIVITY_INGEST'
  },
  disableSnowflakeActivityIngest: {
    doc: 'Stop saving Snowflake Activities',
    format: Boolean,
    default: false,
    env: 'DISABLE_SNOWFLAKE_ACTIVITY_INGEST'
  },
  disableAWSActivityIngest: {
    doc: 'Stop saving AWS Activities',
    format: Boolean,
    default: false,
    env: 'DISABLE_AWS_ACTIVITY_INGEST'
  },
  disableMicrosoftEntraActivityIngest: {
    doc: 'Stop saving Entra Activities',
    format: Boolean,
    default: false,
    env: 'DISABLE_MICROSOFT_ENTRA_ACTIVITY_INGEST'
  },
  disableSlackActivityIngest: {
    doc: 'Stop saving Slack Activities',
    format: Boolean,
    default: false,
    env: 'DISABLE_SLACK_ACTIVITY_INGEST'
  },
  disableWindowsEventForwarding: {
    doc: 'Conditionally sets AD Agent configs to issue commands via RPC - typically only used in development',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_DISABLE_WEF'
  },
  enableInsecureAuthAlert: {
    doc: 'Properly evaluates the insecure authentication detected alert',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_ENABLE_INSECURE_AUTH_ALERT'
  },
  customNotificationsMailer: {
    doc: 'Enables the org notifications subdoc usage and non-sendgrid notifications mailer',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_CUSTOM_NOTIFICATIONS_MAILER'
  },
  enableMDAPIMCPServer: {
    doc: 'Enable access to the MDAPI MCP Server for the organization',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_ENABLE_MDAPI_MCP_SERVER'
  },
  enableDynamicRemoteUrlServers: {
    doc: 'Enable access to DYNAMIC_REMOTE_URL MCP servers for the organization',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_ENABLE_DYNAMIC_REMOTE_URL_SERVERS'
  },
  enableLocalMcpServers: {
    doc: 'Enable access to local MCP servers for the organization',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_ENABLE_LOCAL_MCP_SERVERS'
  },
  showMyAgentsTab: {
    doc: 'Show the My Agents tab under My Connections for non-admin users',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_SHOW_MY_AGENTS_TAB'
  },
  tunnels: {
    doc: 'Enables the Tunnels admin UX and tunnel creation',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_TUNNELS'
  },
  showDownloadDesktopApp: {
    doc: 'Show desktop app download option for orgs with DesktopApp billing enabled',
    format: Boolean,
    default: true,
    env: 'FEATURE_SWITCH_DOWNLOAD_DESKTOP_APP'
  },

  enableContentValidationPolicies: {
    doc: 'Enable content validation policies for MCP tool calls and responses',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_ENABLE_CONTENT_VALIDATION_POLICIES'
  },
  showApplyAndContextFromPolicies: {
    doc: "Show the policy 'what does it apply to' (principal) and 'context conditions' sections. Disabled in the Nova/Snowflake env so policies can't target that env's users.",
    format: Boolean,
    default: true,
    env: 'FEATURE_SWITCH_SHOW_APPLY_AND_CONTEXT_FROM_POLICIES'
  },
  useMcpServersInK8s: {
    doc: 'Route standard MCP server traffic to K8s instead of Cloud Run. Enable per-org for phased rollout.',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_USE_MCP_SERVERS_IN_K8S'
  },
  enableAppsV2: {
    doc: 'Enable the new Apps v2 experience',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_ENABLE_APPS_V2'
  },
  enableAllApps: {
    doc: 'Enable all apps by default and show Apps/Disabled Apps in UI',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_ENABLE_ALL_APPS'
  },
  enableAuthenticatedOnPrem: {
    doc: 'Enable authenticated on-prem servers to be created/managed',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_ENABLE_AUTHENTICATED_ON_PREM'
  },
  actorAwareAuthMethods: {
    doc: 'Filter Get Config dropdowns by actor type (USER vs AGENT).',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_ACTOR_AWARE_AUTH_METHODS'
  },
  enableOpenClawConfig: {
    doc: 'Enable OpenClaw MCP client configuration (~/.openclaw/openclaw.json) in the UI',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_ENABLE_OPENCLAW_CONFIG'
  },
  enableOpenCodeConfig: {
    doc: 'Enable OpenCode MCP client configuration (opencode.json) in the UI',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_ENABLE_OPENCODE_CONFIG'
  },
  enableContinuingWithoutProfile: {
    doc: 'Enable users to continue without selecting a profile',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_ENABLE_CONTINUING_WITHOUT_PROFILE'
  },
  showCombinedProfileUrl: {
    doc: 'Show the combined profile URL option in the connection config UI',
    format: Boolean,
    default: true,
    env: 'FEATURE_SWITCH_SHOW_COMBINED_PROFILE_URL'
  },
  useConnectActionType: {
    doc: 'Gate canConnect policy filtering on connections using the connect action type',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_USE_CONNECT_ACTION_TYPE'
  },
  useProfileAttributesInPolicies: {
    doc: 'Enable the User Attribute option in the policy condition builder',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_USE_PROFILE_ATTRIBUTES_IN_POLICIES'
  },
  shadowAiBatchClassification: {
    doc: 'Use batched Gemini calls for shadow AI MCP classification instead of one call per command',
    format: Boolean,
    default: true,
    env: 'FEATURE_SWITCH_SHADOW_AI_BATCH_CLASSIFICATION'
  },
  enableTacomaFeatures: {
    doc: 'Enable Tacoma features for an org',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_ENABLE_TACOMA_FEATURES'
  },
  shadowAiToolCalls: {
    doc: 'Show tool call history in the Shadow AI detail view for each MCP server installation',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_SHADOW_AI_TOOL_CALLS'
  },
  showOAuthClientConfigDrawer: {
    doc: 'Show the OAuth client credentials configuration drawer in the Apps Card View',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_SHOW_OAUTH_CLIENT_CONFIG_DRAWER'
  },
  showDiscoveredAIClients: {
    doc: 'Show the Discovered AI Clients tab and client detail pages in the Shadow AI discovery view',
    format: Boolean,
    default: true,
    env: 'FEATURE_SWITCH_SHOW_DISCOVERED_AI_CLIENTS'
  },
  showDiscoveredAIMetrics: {
    doc: 'Show the Discovered AI Metrics in the Discovery Dashboard',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_SHOW_DISCOVERED_AI_METRICS'
  },
  showConnectionHealthStatus: {
    doc: 'Show connection health status in the UI for MCP server connections',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_SHOW_CONNECTION_HEALTH_STATUS'
  },
  expandedMcpAuditLogs: {
    doc: 'Emit audit log events for MCP server, profile, policy, deployment, integration, and agent lifecycle actions',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_EXPANDED_MCP_AUDIT_LOGS'
  },
  enableDesktopAppAuditLogs: {
    doc: 'Enable desktop app audit log upload via signed GCS URLs',
    format: Boolean,
    default: false,
    env: 'FEATURE_SWITCH_ENABLE_DESKTOP_APP_AUDIT_LOGS'
  },
  ...serverIdFeatureSwitchObjects
};

export type FeatureSwitchKey = keyof typeof FeatureSwitches;
